<script setup>
import Layout from "../Layouts/Customer.vue";
import { Link } from "@inertiajs/inertia-vue3";
import Button from "primevue/button";
import Card from "primevue/card";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Tag from "primevue/tag";
import { computed } from "vue";

const props = defineProps({
    progressHistory: Object,
    customer: Object,
    device: Object,
    menuItems: Array,
    flash: Object
});

// Format date in German format
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Calculate progress percentage
const getProgressPercentage = (count, limit) => {
    if (!limit || limit === 0) return 0;
    return Math.round((count / limit) * 100);
};

// Get severity color for progress
const getProgressSeverity = (count, limit) => {
    const percentage = getProgressPercentage(count, limit);
    if (percentage >= 100) return 'success';
    if (percentage >= 75) return 'info';
    if (percentage >= 50) return 'warning';
    return 'danger';
};

// Calculate days between resets
const getDaysBetween = (index) => {
    if (index === 0) return '-';
    const current = new Date(props.progressHistory.data[index].created_at);
    const previous = new Date(props.progressHistory.data[index - 1].created_at);
    const diffTime = Math.abs(current - previous);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} Tage`;
};

// Check if there are more pages
const hasMorePages = computed(() => {
    return props.progressHistory.last_page > 1;
});

// Determine if a page number should be shown
const showPageNumber = (page) => {
    const current = props.progressHistory.current_page;
    const last = props.progressHistory.last_page;
    
    // Always show first and last page
    if (page === 1 || page === last) return true;
    
    // Show pages near current page (within 2 pages)
    if (Math.abs(page - current) <= 2) return true;
    
    return false;
};

// Determine if ellipsis should be shown after this page
const showEllipsis = (page) => {
    const current = props.progressHistory.current_page;
    const last = props.progressHistory.last_page;
    
    // Show ellipsis if there's a gap after page 1
    if (page === 2 && current > 4) return true;
    
    // Show ellipsis if there's a gap before last page
    if (page === last - 1 && current < last - 3) return true;
    
    return false;
};
</script>

<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <div class="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            <!-- Header -->
            <div class="mb-8">
                <div class="flex justify-between items-center">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-800">Progress History</h1>
                        <p class="text-gray-600 mt-1">
                            {{ customer.first_name }} {{ customer.last_name }} - {{ device.name }}
                        </p>
                    </div>
                    <Link 
                        :href="`/customers/${customer.uuid}/trainingsplan`"
                        class="text-blue-600 hover:text-blue-800 underline"
                    >
                        ← Zurück zum Trainingsplan
                    </Link>
                </div>
            </div>

            <!-- History Table -->
            <Card v-if="progressHistory.data.length > 0">
                <template #content>
                    <DataTable 
                        :value="progressHistory.data" 
                        responsiveLayout="scroll"
                        class="p-datatable-sm"
                        stripedRows
                    >
                        <Column field="created_at" header="Reset Datum" :sortable="false">
                            <template #body="slotProps">
                                <span class="font-medium">{{ formatDate(slotProps.data.created_at) }}</span>
                            </template>
                        </Column>

                        <Column field="count" header="Fortschritt" :sortable="false">
                            <template #body="slotProps">
                                <div class="flex items-center gap-2">
                                    <span class="font-bold">{{ slotProps.data.count }}/{{ slotProps.data.limit }}</span>
                                    <Tag 
                                        :severity="getProgressSeverity(slotProps.data.count, slotProps.data.limit)"
                                        :value="`${getProgressPercentage(slotProps.data.count, slotProps.data.limit)}%`"
                                    />
                                </div>
                            </template>
                        </Column>
                    </DataTable>

                    <!-- Pagination -->
                    <div v-if="hasMorePages" class="flex justify-between items-center mt-4 pt-4 border-t">
                        <div class="text-sm text-gray-600">
                            Zeige {{ progressHistory.from }} bis {{ progressHistory.to }} von {{ progressHistory.total }} Einträgen
                        </div>
                        <div class="flex gap-1">
                            <!-- First Page -->
                            <Link 
                                v-if="progressHistory.current_page > 1"
                                :href="progressHistory.first_page_url"
                                class="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
                                title="Erste Seite"
                            >
                                «
                            </Link>
                            
                            <!-- Previous -->
                            <Link 
                                v-if="progressHistory.prev_page_url"
                                :href="progressHistory.prev_page_url"
                                class="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
                            >
                                ‹
                            </Link>
                            
                            <!-- Page Numbers -->
                            <template v-for="page in progressHistory.last_page" :key="page">
                                <Link
                                    v-if="showPageNumber(page)"
                                    :href="`?page=${page}`"
                                    :class="[
                                        'px-3 py-2 rounded text-sm',
                                        page === progressHistory.current_page 
                                            ? 'bg-blue-500 text-white' 
                                            : 'bg-gray-100 hover:bg-gray-200'
                                    ]"
                                >
                                    {{ page }}
                                </Link>
                                <span 
                                    v-else-if="showEllipsis(page)"
                                    class="px-2 py-2 text-gray-500"
                                >
                                    ...
                                </span>
                            </template>
                            
                            <!-- Next -->
                            <Link 
                                v-if="progressHistory.next_page_url"
                                :href="progressHistory.next_page_url"
                                class="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
                            >
                                ›
                            </Link>
                            
                            <!-- Last Page -->
                            <Link 
                                v-if="progressHistory.current_page < progressHistory.last_page"
                                :href="progressHistory.last_page_url"
                                class="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
                                title="Letzte Seite"
                            >
                                »
                            </Link>
                        </div>
                    </div>
                </template>
            </Card>

            <!-- Empty State -->
            <Card v-else>
                <template #content>
                    <div class="text-center py-12">
                        <i class="pi pi-history text-4xl text-gray-400 mb-4"></i>
                        <h3 class="text-lg font-medium text-gray-900 mb-2">Keine Historie vorhanden</h3>
                        <p class="text-gray-600">
                            Es wurden noch keine Fortschritte für dieses Gerät zurückgesetzt.
                        </p>
                        <Link 
                            :href="`/customers/${customer.uuid}/trainingsplan`"
                            class="mt-4 inline-block text-blue-600 hover:text-blue-800 underline"
                        >
                            Zum Trainingsplan
                        </Link>
                    </div>
                </template>
            </Card>
        </div>
    </Layout>
</template>

<style scoped>
:deep(.p-datatable .p-datatable-thead > tr > th) {
    background-color: #f9fafb;
    font-weight: 600;
}

:deep(.p-tag) {
    font-size: 0.75rem;
}
</style>